import { GetPaginatedTendersQueryVariables } from "@/graphql/__generated__/graphql";
import { updatePaginationState, usePaginationState } from "@/store/paginationStore";
import { handleTenderStateColour, getUserFriendlyTenderState, formatDateLocale, formatNumberLocale, convertBpToDecimal } from "@/utils/helpers";
import { t } from "i18next";
import FilterCard from "./FilterCard";
import { useIntl } from "react-intl";
import { Badge } from "@/components/ui/badge";
import { JSX } from "react";
import { cn } from "@/lib/utils";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: GetPaginatedTendersQueryVariables) => void;
  handleResetClickFilters: (_preserveSort?: boolean) => void;
}

/**
 * ActiveFilters component
 * Displays and manages active filters for the Tenders page
 * Allows users to view and remove applied filters
 *
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - Number of items to display per page
 * @param {Function} props.handleSubmitClick - Function to handle submitting updated query variables
 * @param {Function} props.handleResetClickFilters - Function to reset all filters
 * @returns {JSX.Element | null} The active filters component or null if no filters are active
 */
const ActiveFilters = (props: Props): JSX.Element | null => {

  // Props
  const { displayAmount, handleSubmitClick, handleResetClickFilters } = props;

  // Get pagination state from global store
  const paginationState: PaginationState["paginatedTenders"] = usePaginationState("paginatedTenders");
  const { filters: {
    archived,
    startDate,
    deadlineDate,
    state,
    search,
    operationalOrgs,
    chanceAdmins,
    revenue,
    surcharge,
  } } = paginationState as any;

  // Internationalization hook for formatting
  const intl = useIntl();

  /**
   * Helper function to check if a value is valid (not null or undefined)
   * Used for validating revenue and surcharge filter values
   *
   * @param {number | null | undefined} value - The value to check
   * @returns {boolean} True if the value is valid (including 0)
   */
  const isValid = (value: number | null | undefined): boolean => {
    return typeof value === "number" && !isNaN(value);
  };

  // Check if any filters are active to determine whether to render the component
  const hasActiveFilters =
    archived === true ||
    (search && search.length > 0) ||
    (state && state.length > 0) ||
    (startDate?.gte || startDate?.lte) ||
    (deadlineDate?.gte || deadlineDate?.lte) ||
    (operationalOrgs && operationalOrgs.length > 0) ||
    (chanceAdmins && chanceAdmins.length > 0) ||
    (isValid(revenue?.gte) || isValid(revenue?.lte)) ||
    (isValid(surcharge?.gte) || isValid(surcharge?.lte));

  // Don't render anything if no filters are active
  if (!hasActiveFilters) return null;

  /**
   * Helper function to check if filters will be empty after an update
   * Used to determine whether to reset all filters or just update specific ones
   *
   * @param {Record<string, any>} filterUpdate - The filter update to apply
   * @returns {boolean} True if all filters will be empty after the update
   */
  const willFiltersBeEmpty = (filterUpdate: Record<string, any>) => {
    // Create a copy of the current filters with the update applied
    const updatedFilters = {
      archived: filterUpdate.archived !== undefined ? filterUpdate.archived : archived,
      search: filterUpdate.search !== undefined ? filterUpdate.search : search,
      state: filterUpdate.state !== undefined ? filterUpdate.state : state,
      startDate: filterUpdate.startDate !== undefined ? filterUpdate.startDate : startDate,
      deadlineDate: filterUpdate.deadlineDate !== undefined ? filterUpdate.deadlineDate : deadlineDate,
      operationalOrgs: filterUpdate.operationalOrgs !== undefined ? filterUpdate.operationalOrgs : operationalOrgs,
      chanceAdmins: filterUpdate.chanceAdmins !== undefined ? filterUpdate.chanceAdmins : chanceAdmins,
      revenue: filterUpdate.revenue !== undefined ? filterUpdate.revenue : revenue,
      surcharge: filterUpdate.surcharge !== undefined ? filterUpdate.surcharge : surcharge,
    };

    // Check if any filters are still active after the update
    return !(
      updatedFilters.archived === true ||
      (updatedFilters.search && updatedFilters.search.length > 0) ||
      (updatedFilters.state && updatedFilters.state.length > 0) ||
      (updatedFilters.startDate?.gte || updatedFilters.startDate?.lte) ||
      (updatedFilters.deadlineDate?.gte || updatedFilters.deadlineDate?.lte) ||
      (updatedFilters.operationalOrgs && updatedFilters.operationalOrgs.length > 0) ||
      (updatedFilters.chanceAdmins && updatedFilters.chanceAdmins.length > 0) ||
      (isValid(updatedFilters.revenue?.gte) || isValid(updatedFilters.revenue?.lte)) ||
      (isValid(updatedFilters.surcharge?.gte) || isValid(updatedFilters.surcharge?.lte))
    );
  };

  /**
   * Removes a filter and triggers data refresh
   * Updates the pagination state and submits the updated filters
   *
   * @param {string} filterKey - The key of the filter to remove
   */
  const removeFilterAndSubmit = (filterKey: string) => {
    // Get the current complete filters object
    const currentFilters = {
      archived,
      startDate,
      deadlineDate,
      state,
      search,
      operationalOrgs,
      chanceAdmins,
      revenue,
      surcharge,
    };

    // Create updated filters with the specified filter removed
    const updatedFilters = {
      ...currentFilters,
      [filterKey]: null
    };

    // Check if this will be the last active filter
    const willBeEmpty = willFiltersBeEmpty({ [filterKey]: null });

    // Update the pagination state with the complete updated filters
    updatePaginationState({ paginatedTenders: { filters: updatedFilters } });

    // If this is the last filter, trigger reset instead of submit, but preserve sorting
    if (willBeEmpty) {
      // Use setTimeout to ensure state is updated before submitting
      setTimeout(() => handleResetClickFilters(true), 0);
    } else {
      // Prepare query variables with the updated filters wrapped in filters object
      const queryVariables: GetPaginatedTendersQueryVariables = {
        first: displayAmount,
        filters: updatedFilters,
      };

      // Use setTimeout to ensure state is updated before submitting
      setTimeout(() => handleSubmitClick(queryVariables), 0);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {/* Archived Filter */}
      {archived === true && (
        <FilterCard
          label={t("components:filters.tenders.archived.showingarchived")}
          content={<span className="text-xs">{t("components:filters.tenders.archived.showingarchived")}</span>}
          onRemove={() => removeFilterAndSubmit("archived")}
        />
      )}

      {/* Search Filter */}
      {search && search.length > 0 && (
        <FilterCard
          label={t("components:filters.tenders.search")}
          content={<span className="text-xs">{search}</span>}
          onRemove={() => removeFilterAndSubmit("search")}
        />
      )}

      {/* State Filters */}
      {state && state.length > 0 && (
        <FilterCard
          label={t("components:filters.tenders.chancestate")}
          content={
            <div className="flex flex-wrap gap-1">
              {state.map(s => (
                <Badge key={s} variant="outline" className={cn("text-xs py-0 h-5 text-white border-0", handleTenderStateColour(s, true, true, false))}>
                  {getUserFriendlyTenderState(t, s)}
                </Badge>
              ))}
            </div>
          }
          onRemove={() => removeFilterAndSubmit("state")}
        />
      )}

      {/* Start Date Filter */}
      {(startDate?.gte || startDate?.lte) && (
        <FilterCard
          label={t("common:startdate")}
          content={
            <span className="text-xs">
              <span className="font-medium mr-1">{t("common:startdate")}:</span>
              {startDate?.gte ? formatDateLocale(intl, startDate.gte) : ""}
              {startDate?.gte && startDate?.lte ? " - " : ""}
              {startDate?.lte ? formatDateLocale(intl, startDate.lte) : ""}
            </span>
          }
          onRemove={() => removeFilterAndSubmit("startDate")}
        />
      )}

      {/* Deadline Filter */}
      {(deadlineDate?.gte || deadlineDate?.lte) && (
        <FilterCard
          label={t("common:deadline")}
          content={
            <span className="text-xs">
              <span className="font-medium mr-1">{t("common:deadline")}:</span>
              {deadlineDate?.gte ? formatDateLocale(intl, deadlineDate.gte) : ""}
              {deadlineDate?.gte && deadlineDate?.lte ? " - " : ""}
              {deadlineDate?.lte ? formatDateLocale(intl, deadlineDate.lte) : ""}
            </span>
          }
          onRemove={() => removeFilterAndSubmit("deadlineDate")}
        />
      )}

      {/* Operational Orgs Filter */}
      {operationalOrgs && operationalOrgs.length > 0 && (
        <FilterCard
          label={t("components:filters.tenders.operationalorgs")}
          content={
            <span className="text-xs">
              {t("components:filters.tenders.operationalorgs")}: {operationalOrgs.length}
            </span>
          }
          onRemove={() => removeFilterAndSubmit("operationalOrgs")}
        />
      )}

      {/* Chance Admins Filter */}
      {chanceAdmins && chanceAdmins.length > 0 && (
        <FilterCard
          label={t("components:filters.tenders.chanceadmins")}
          content={
            <span className="text-xs">
              {t("components:filters.tenders.chanceadmins")}: {chanceAdmins.length}
            </span>
          }
          onRemove={() => removeFilterAndSubmit("chanceAdmins")}
        />
      )}

      {/* Revenue Filter */}
      {(isValid(revenue?.gte) || isValid(revenue?.lte)) && (
        <FilterCard
          label={t("components:filters.tenders.revenue")}
          content={
            <span className="text-xs">
              {isValid(revenue?.gte) && !revenue?.lte
                ? <>
                  <span className="font-medium mr-1">
                    {t("common:from")}
                  </span>{formatNumberLocale(intl, revenue.gte as number, "currency", true)}
                </>
                : isValid(revenue?.gte)
                  ? formatNumberLocale(intl, revenue.gte as number, "currency", true)
                  : ""}

              {isValid(revenue?.gte) && isValid(revenue?.lte) ? " - " : ""}

              {isValid(revenue?.lte) && !revenue?.gte
                ? <>
                  <span className="font-medium mr-1">
                    {t("common:to")}
                  </span>{formatNumberLocale(intl, revenue.lte as number, "currency", true)}
                </>
                : isValid(revenue?.lte)
                  ? formatNumberLocale(intl, revenue.lte as number, "currency", true)
                  : ""}
            </span>
          }
          onRemove={() => removeFilterAndSubmit("revenue")}
        />
      )}

      {/* Surcharge Filter */}
      {(isValid(surcharge?.gte) || isValid(surcharge?.lte)) && (
        <FilterCard
          label={t("components:filters.tenders.surcharge")}
          content={
            <span className="text-xs">
              {isValid(surcharge?.gte) && !surcharge?.lte
                ? <>
                  <span className="font-medium mr-1">
                    {t("common:from")}
                  </span>{formatNumberLocale(intl, convertBpToDecimal(surcharge.gte as number), "decimal", true)}%
                </>
                : isValid(surcharge?.gte)
                  ? `${formatNumberLocale(intl, convertBpToDecimal(surcharge.gte as number), "decimal", true)}%`
                  : ""}

              {isValid(surcharge?.gte) && isValid(surcharge?.lte) ? " - " : ""}

              {isValid(surcharge?.lte) && !surcharge?.gte
                ? <>
                  <span className="font-medium mr-1">
                    {t("common:to")}
                  </span>{formatNumberLocale(intl, convertBpToDecimal(surcharge.lte as number), "decimal", true)}%
                </>
                : isValid(surcharge?.lte)
                  ? `${formatNumberLocale(intl, convertBpToDecimal(surcharge.lte as number), "decimal", true)}%`
                  : ""}
            </span>
          }
          onRemove={() => removeFilterAndSubmit("surcharge")}
        />
      )}

    </div>
  );
};

export default ActiveFilters;
